<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">轮播图</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 张</span>
        <span class="divider">|</span>
        <span>上架 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>下架 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input v-model="query.keyword" placeholder="标题关键词" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="位置">
            <el-select v-model="query.position" placeholder="全部" clearable style="width: 150px">
              <el-option v-for="d in positions" :key="d._id" :label="d.label" :value="String(d._id)" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="s in BANNER_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增轮播图</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 缩略图 -->
        <el-table-column label="图片" width="100" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.image" class="banner-thumb">
              <el-image
                :src="scope.row.image"
                :preview-src-list="[scope.row.image]"
                fit="cover"
                class="banner-thumb__img"
              />
            </div>
            <div v-else class="banner-thumb banner-thumb--empty">无图</div>
          </template>
        </el-table-column>
        <!-- 位置 -->
        <el-table-column label="位置" width="100" align="center">
          <template slot-scope="scope">{{ positionLabel(scope.row.position) }}</template>
        </el-table-column>
        <!-- 标题 -->
        <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.title">{{ scope.row.title }}</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 跳转类型 -->
        <el-table-column label="跳转类型" width="110" align="center">
          <template slot-scope="scope">{{ bannerLinkTypeLabel(scope.row.link_type) }}</template>
        </el-table-column>
        <!-- 跳转值 -->
        <el-table-column label="跳转值" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.link_type === 0" class="cell-muted">—</span>
            <span v-else class="cell-link">{{ linkValueText(scope.row) }}</span>
          </template>
        </el-table-column>
        <!-- 排序 -->
        <el-table-column prop="sort" label="排序" width="70" align="center" />
        <!-- 状态 -->
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="bannerStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ bannerStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="240" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status === 0"
              type="text" size="mini"
              @click="handlePublish(scope.row)"
            >上架</el-button>
            <el-button
              v-else
              type="text" size="mini" class="text-warning-btn"
              @click="handleOffline(scope.row)"
            >下架</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 编辑/新增弹框 -->
    <el-dialog
      :title="isEdit ? '编辑轮播图' : '新增轮播图'"
      :visible.sync="formVisible"
      width="780px"
      top="6vh"
      class="banner-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <div class="form-section">
          <div class="form-section-title">基础</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="位置" prop="position">
                <el-select v-model="form.position" placeholder="请选择" style="width: 100%">
                  <el-option v-for="d in positions" :key="d._id" :label="d.label" :value="String(d._id)" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="标题" prop="title">
                <el-input v-model="form.title" placeholder="纯图轮播可留空" maxlength="64" show-word-limit />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="轮播图" prop="image">
            <image-uploader v-model="form.image" />
            <span class="form-tip">{{ sizeTip }}</span>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">跳转</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="跳转类型" prop="link_type">
                <el-select v-model="form.link_type" placeholder="请选择" style="width: 100%">
                  <el-option v-for="t in BANNER_LINK_TYPE" :key="t.value" :label="t.label" :value="t.value" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="跳转值" prop="link_value">
                <!-- 0 不跳：灰态 -->
                <el-input
                  v-if="form.link_type === 0"
                  value="—"
                  disabled
                />
                <!-- 1 活动：选活动 -->
                <el-select
                  v-else-if="form.link_type === 1"
                  v-model="form.link_value"
                  placeholder="请选择活动"
                  style="width: 100%"
                  filterable
                >
                  <el-option
                    v-for="a in activityOptions"
                    :key="a.id"
                    :label="`#${a.id} ${a.name}`"
                    :value="String(a.id)"
                  />
                </el-select>
                <!-- 6 H5 外链：url -->
                <el-input
                  v-else-if="form.link_type === 6"
                  v-model="form.link_value"
                  placeholder="https:// 开头"
                />
                <!-- 7 小程序内页：路径 -->
                <el-input
                  v-else-if="form.link_type === 7"
                  v-model="form.link_value"
                  placeholder="/pages/xxx/xxx"
                />
                <!-- 2/3/4/5 暂无数据源：通用 ID 输入 -->
                <el-input
                  v-else
                  v-model="form.link_value"
                  :placeholder="linkValuePlaceholder"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-section">
          <div class="form-section-title">排序与状态</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="排序" prop="sort">
                <el-input-number v-model="form.sort" :min="0" :max="999" controls-position="right" style="width: 140px" />
                <span class="form-tip">数字越小越靠前</span>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="状态" prop="status">
                <el-radio-group v-model="form.status">
                  <el-radio :label="1">上架</el-radio>
                  <el-radio :label="0">下架</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 状态/跳转类型的显示映射（前端展示用，随 mock 目录统一维护；数据已走后端接口）
import {
  BANNER_LINK_TYPE, BANNER_STATUS,
  bannerLinkTypeLabel, bannerStatusText, bannerStatusTagType, bannerLinkValueText
} from '@/mock/banner'
import {
  getBannerList, deleteBanner, createBanner, updateBanner, updateBannerStatus
} from '@/api/banner'
import { getActivityList } from '@/api/activity'
import dictOptions from '@/mixins/dict-options'
import ImageUploader from '@/components/ImageUploader.vue'

export default {
  name: 'BannerManage',
  components: { ImageUploader },
  mixins: [dictOptions],
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      positions: [],
      activityOptions: [],
      query: { keyword: '', position: '', status: '' },
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      rules: {
        position: [{ required: true, message: '请选择位置', trigger: 'change' }],
        image: [{ required: true, message: '请上传轮播图', trigger: 'change' }],
        link_type: [{ required: true, message: '请选择跳转类型', trigger: 'change' }],
        sort: [{ required: true, message: '请输入排序值', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      BANNER_LINK_TYPE,
      BANNER_STATUS
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) },
    // 跳转值 placeholder：1/2/3/4/5 都是 ID 输入，placeholder 区分
    linkValuePlaceholder() {
      return {
        2: '官方内容 ID',
        3: '景区 ID',
        4: '旅居基地 ID',
        5: '社区帖子 ID'
      }[this.form.link_type] || ''
    },
    // 尺寸提示（仅提示不限制）：首页 750×946，其他位置 750×248
    sizeTip() {
      const label = this.positionLabel(this.form.position)
      if (label.indexOf('首页') !== -1) return '建议尺寸 750×946'
      if (!this.form.position) return '建议尺寸：首页 750×946，其他位置 750×248'
      return '建议尺寸 750×248'
    }
  },
  created() {
    this.loadData()
    this.loadDictOptions({ banner_position: 'positions' })
    // 活动下拉与列表「跳转值」列的 id→名称映射共用一份
    this.loadActivityOptions()
  },
  methods: {
    // 列表：服务端分页 + 筛选（keyword 模糊 / position、status 精确；后端按 位置→状态→排序值 排列）
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const keyword = (this.query.keyword || '').trim()
      if (keyword) params.keyword = keyword
      if (this.query.position) params.position = this.query.position
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getBannerList(params).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    handlePageChange(p) {
      this.page = p
      this.loadData()
    },
    statusCount(val) { return this.list.filter(i => i.status === val).length },
    positionLabel(value) {
      const item = this.positions.find(p => String(p._id) === String(value))
      return item ? item.label : (value || '—')
    },
    bannerLinkTypeLabel,
    bannerStatusText,
    bannerStatusTagType,
    bannerLinkValueText,
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', position: '', status: '' }
      this.page = 1
      this.loadData()
    },
    // 活动下拉数据源：真接口拉一页，过滤掉草稿；首次打开表单时才拉
    loadActivityOptions() {
      if (this.activityOptions.length) return
      getActivityList({ page: 1, page_size: 100 }).then(res => {
        if (res.code !== 200) return
        this.activityOptions = (res.data.list || []).filter(a => a.status !== 0)
      }).catch(() => {})
    },
    // 跳转值列展示：活动带名称「活动 #12 名称」，其余类型沿用枚举映射
    linkValueText(row) {
      if (row.link_type === 1) {
        const a = this.activityOptions.find(i => String(i.id) === String(row.link_value))
        if (a) return `活动 #${row.link_value} ${a.name}`
      }
      return bannerLinkValueText(row)
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.formVisible = true
      this.loadActivityOptions()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      this.form = Object.assign(this.emptyForm(), row)
      this.formVisible = true
      this.loadActivityOptions()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handlePublish(row) {
      this.$confirm(`确认上架这张轮播图？上架后小程序端可见。`, '上架确认', { type: 'warning' })
        .then(() => updateBannerStatus(row.id, { status: 1 }))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已上架')
          this.loadData()
        }).catch(() => {})
    },
    handleOffline(row) {
      this.$confirm(`确认下架这张轮播图？下架后小程序端不再展示。`, '下架确认', { type: 'warning' })
        .then(() => updateBannerStatus(row.id, { status: 0 }))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已下架')
          this.loadData()
        }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.title || '该'}轮播图」？`, '删除确认', { type: 'warning' })
        .then(() => deleteBanner(row.id))
        .then(res => {
          if (res.code !== 200) return
          this.$message.success('已删除')
          // 当前页删空时回退一页
          if (this.list.length === 1 && this.page > 1) this.page -= 1
          this.loadData()
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = {
          position: this.form.position,
          title: this.form.title,
          image: this.form.image,
          link_type: this.form.link_type,
          link_value: this.form.link_value,
          sort: this.form.sort,
          status: this.form.status
        }
        this.submitting = true
        const req = this.isEdit
          ? updateBanner(this.currentRow.id, payload)
          : createBanner(payload)
        req.then(res => {
          if (res.code !== 200) return
          this.$message.success(this.isEdit ? '已保存' : '已新增')
          this.formVisible = false
          this.loadData()
        }).catch(() => {}).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyForm() {
      return {
        position: '',
        title: '',
        image: '',
        link_type: 0,
        link_value: '',
        sort: 1,
        status: 0
      }
    }
  }
}
</script>

<style scoped>
.cell-sub {
  font-size: 12px;
  margin-top: 2px;
}
.cell-link {
  color: var(--primary, #8e1103);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

/* 缩略图 */
.banner-thumb {
  width: 80px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.banner-thumb img,
.banner-thumb .banner-thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner-thumb .banner-thumb__img {
  cursor: zoom-in;
}
.banner-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
  border: 1px dashed #dcdfe6;
  background: #fafafa;
}

/* 分段标题（与活动表单一致） */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  white-space: nowrap;
}

/* 弹框内整体样式（与全局弹框适配） */
.banner-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
</style>
